'use client';

import type { ReactElement } from 'react';
import React from 'react';
import type { ItemProps } from 'react-stately';
import type { PartialNode } from '@react-stately/collections';
import type { CarouselVariant } from './types';

type Context = { variant: CarouselVariant };

type CarouselItemProps<T> = Omit<ItemProps<T>, 'routerOptions'> & {
  as?: React.ElementType;
};

function CarouselItem<T>(__props: CarouselItemProps<T>): ReactElement | null {
  return null;
}

CarouselItem.getCollectionNode = function* getCollectionNode<T>(
  props: CarouselItemProps<T>,
  context: Context
): Generator<PartialNode<T>> {
  const { children, textValue, childItems } = props;
  const { variant = 'unstyled' } = context;

  yield {
    type: 'item',
    props: {
      ...props,
      variant,
    },
    rendered: children,
    'aria-label': props['aria-label'],
    hasChildNodes: hasChildItems(props),
    textValue,
    *childNodes() {
      if (childItems) {
        // eslint-disable-next-line no-restricted-syntax
        for (const child of childItems) {
          yield {
            type: 'item',
            value: child,
          };
        }
      }
    },
  };
};

function hasChildItems<T>(props: ItemProps<T>) {
  if (props.hasChildItems != null) {
    return props.hasChildItems;
  }

  if (props.childItems) {
    return true;
  }

  if (props.title && React.Children.count(props.children) > 0) {
    return true;
  }

  return false;
}

type PolymorphicProps<E extends React.ElementType> = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<E> & {
    as?: E;
  }
>;

type CarouselItemPropsWithAs<
  T extends React.ElementType,
  P
> = PolymorphicProps<T> & CarouselItemProps<P>;
const _CarouselItem = CarouselItem as <T extends React.ElementType, P>(
  props: CarouselItemPropsWithAs<T, P>
) => ReactElement<any>;
export { _CarouselItem as CarouselItem };
